import {
  IEMRStaticDurationResponseData,
  IEMRStaticMedicineFrequencyResponseData,
  IEMRStaticMedicineTimingResponseData,
  IEMRStaticRoutesResponseData,
  IEMRStaticUnitsResponseData,
} from "@/api_framework/api_modals/EMRs";
import Input from "@/app/components/ui/Inputs/Input";
import InputWithSuggestionBox from "@/app/components/ui/Inputs/InputWithSuggestionBox";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { EMRModeType, ICustomerEMRState, setTaperingIndex } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import type { IEMRMedicationSectionFormFields } from "./MedicinesSectionV2";
import { useEffect, useMemo, useRef } from "react";

const MEDICINE_DROPDOWN_Z_INDEX = 20;
const hasFilledValue = (value: unknown) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return true;
  return String(value).trim() !== "";
};
const DURATION_NUMBER_FORMAT_VALIDATION = {
  pattern: {
    value: /^-?\d+(\.\d+)?$/,
    message: "Only numeric values",
  },
};
const DURATION_NUMERIC_ERROR_MESSAGE = { message: "Only numeric values", type: "pattern" as const };
const DURATION_NO_DECIMAL_ERROR_MESSAGE = { message: "No decimal values", type: "noDecimal" as const };
const DURATION_POSITIVE_ERROR_MESSAGE = { message: "Only positive values", type: "positiveValue" as const };
const DOSE_DECIMAL_VALIDATION = {
  pattern: {
    value: /^-?\d+(\.\d{1,2})?$/,
    message: "Upto 2 decimals only",
  },
};
const DOSE_DECIMAL_ERROR_MESSAGE = { message: "Upto 2 decimals only", type: "pattern" as const };
const DOSE_POSITIVE_ERROR_MESSAGE = { message: "Only positive values", type: "validate" as const };

const NestedTaperingDoseV2: React.FC<{
  units: IEMRStaticUnitsResponseData[];
  frequencies: IEMRStaticMedicineFrequencyResponseData[];
  durations: IEMRStaticDurationResponseData[];
  timings: IEMRStaticMedicineTimingResponseData[];
  routes: IEMRStaticRoutesResponseData[];
  mode: EMRModeType;
  formHook: UseFormReturn<IEMRMedicationSectionFormFields, any>;
  nestedIndex: number;
  isParentSkuFormMandatory?: boolean;
  dropdownZIndex?: number;
}> = ({
  formHook,
  nestedIndex,
  mode,
  durations,
  frequencies,
  timings,
  units,
  isParentSkuFormMandatory = false,
  dropdownZIndex = MEDICINE_DROPDOWN_Z_INDEX,
}) => {
  const unitItems = useSelectMenuReducer(units, "dose_unit_name", "dose_unit_name");
  const frequencySuggestionGroups = useMemo(
    () =>
      (frequencies || [])
        .map((group) => ({
          title: group.frequency_category,
          options: (group.options || []).map((option) => option.frequency_name).filter(Boolean),
        }))
        .filter((group) => group.options.length > 0),
    [frequencies],
  );
  const frequencySuggestions = useMemo(
    () => frequencySuggestionGroups.flatMap((group) => group.options),
    [frequencySuggestionGroups],
  );
  const frequencyXValueRequirementMap = useMemo(
    () =>
      (frequencies || []).reduce(
        (acc, group) => {
          (group.options || []).forEach((option) => {
            if (option?.frequency_name) {
              acc[option.frequency_name] = !!option?.x_value;
            }
          });
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [frequencies],
  );
  const timingSuggestionGroups = useMemo(
    () =>
      (timings || [])
        .map((group) => ({
          title: group.timing_category,
          options: (group.options || []).map((option) => option.timing_name).filter(Boolean),
        }))
        .filter((group) => group.options.length > 0),
    [timings],
  );
  const timingSuggestions = useMemo(
    () => timingSuggestionGroups.flatMap((group) => group.options),
    [timingSuggestionGroups],
  );
  const doseRef = useRef<HTMLInputElement>(null);
  const frequencyRef = useRef<HTMLInputElement>(null);
  const timingRef = useRef<HTMLInputElement>(null);
  const remarkRef = useRef<HTMLInputElement>(null);
  const { getRouteKey } = useRouter();
  const dispatch = useAppDispatch();
  const { taperingIndex } = useRouteInfo(getRouteKey("CUSTOMER_EMR", "id")).routeState.state as ICustomerEMRState;
  const fieldArrayHook = useFieldArray({
    name: `medications.${nestedIndex}.tapering_doses` as const,
    control: formHook.control,
  });
  const parentMedication = formHook.getValues(`medications.${nestedIndex}` as any) as any;
  const parentSkuForm = parentMedication?.sku_form?.trim?.() || null;
  const hasParentSkuForm = !!parentSkuForm;

  useEffect(() => {
    if (!hasParentSkuForm) return;

    const taperingDoses = formHook.getValues(`medications.${nestedIndex}.tapering_doses` as any) || [];
    taperingDoses.forEach((_: any, taperingIndex: number) => {
      const doseUnitPath = `medications.${nestedIndex}.tapering_doses.${taperingIndex}.dose_unit` as any;
      const currentDoseUnit = formHook.getValues(doseUnitPath);
      if (currentDoseUnit !== parentSkuForm) {
        formHook.setValue(doseUnitPath, parentSkuForm, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
  }, [fieldArrayHook.fields.length, formHook, hasParentSkuForm, nestedIndex, parentSkuForm]);

  useAfterFirstRender(() => {
    if (taperingIndex < 0) return;
    if (taperingIndex >= 0) {
      if (taperingIndex === nestedIndex) {
        fieldArrayHook.append({
          quantity: 1,
          duration_unit: "days",
          dose_unit: hasParentSkuForm ? parentSkuForm : undefined,
        });
        dispatch(setTaperingIndex(-1));
      }
    }
  }, [hasParentSkuForm, parentSkuForm, taperingIndex]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, nextRef: React.RefObject<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  return fieldArrayHook?.fields?.map((field, index) => {
    const selectedDose = formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.dose`);
    const selectedFrequency = formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.frequency`);
    const selectedTiming = formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.timing`);
    const selectedDuration = formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.duration`);
    const selectedXValue = formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.x_value`);
    const showXValueInput = !!frequencyXValueRequirementMap[selectedFrequency];
    const isDoseRequiredAndEmpty = isParentSkuFormMandatory && !hasFilledValue(selectedDose);
    const isFrequencyRequiredAndEmpty = isParentSkuFormMandatory && !hasFilledValue(selectedFrequency);
    const isTimingRequiredAndEmpty = isParentSkuFormMandatory && !hasFilledValue(selectedTiming);
    const isDurationRequiredAndEmpty = isParentSkuFormMandatory && !hasFilledValue(selectedDuration);
    const isXRequiredAndEmpty = isParentSkuFormMandatory && showXValueInput && !hasFilledValue(selectedXValue);
    return (
      <tr id={field.id}>
        <td className="">
          <div className="w-full flex justify-end items-center text-xs text-amber-600 pr-2 ">
            Tapering Dosage {index + 1}
          </div>
        </td>
        <td>
          <div className="w-full py-2 px-2 sm:px-3">
            <div className="relative rounded-md flex items-center gap-3">
              {mode === "PDF_PREVIEW" ? (
                <span className="w-32 text-xs flex justify-start items-center">
                  {formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.dose`)}{" "}
                  {formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.dose_unit`)}
                </span>
              ) : (
                <div className="relative w-20">
                  <Input
                    data-medicine-required-empty={isDoseRequiredAndEmpty ? "true" : undefined}
                    errorMessages={[
                      { message: "", type: "required" },
                      DOSE_DECIMAL_ERROR_MESSAGE,
                      DOSE_POSITIVE_ERROR_MESSAGE,
                    ]}
                    fieldError={formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.dose}
                    className="w-full border-[#E2E2E2] "
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    variant="TRANSPARENT"
                    inputClassName={`ring-1 focus:border-1 focus:ring-1 h-8 ${
                      isDoseRequiredAndEmpty ? "!ring-red-500 !focus:ring-red-500" : ""
                    }`}
                    placeholder={"e.g 10"}
                    register={formHook.register(`medications.${nestedIndex}.tapering_doses.${index}.dose`, {
                      required: isParentSkuFormMandatory,
                      ...DOSE_DECIMAL_VALIDATION,
                      validate: (value) => {
                        if (value === null || value === undefined || String(value).trim() === "") {
                          return !isParentSkuFormMandatory;
                        }
                        return Number(value) > 0;
                      },
                    })}
                    onKeyDown={(e) => handleKeyPress(e, doseRef)}
                  />
                </div>
              )}

              <div>
                {hasParentSkuForm ? (
                  <div className="min-w-[84px] sm:min-w-[96px] text-xs">
                    <p className="text-[10px] font-semibold tracking-[0.14em] text-[#9CA3AF] uppercase mb-1">
                      Auto
                    </p>
                    <p className="h-6 px-3 rounded-lg bg-[#F2F2F2] text-[#4B5563] text-xs font-semibold flex items-center">
                      <span>
                        {formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.dose_unit`) || parentSkuForm}
                      </span>
                    </p>
                  </div>
                ) : (
                  <div>
                    <InputWithSuggestionBox
                      dropdownZIndex={dropdownZIndex}
                      showAllSuggestionsOnFocus={true}
                      ref={doseRef}
                      suggestions={unitItems.map((item) => item.title)}
                      placeholder="Dose Unit"
                      defaultInputValue={formHook.getValues(
                        `medications.${nestedIndex}.tapering_doses.${index}.dose_unit`,
                      )}
                      formErrors={
                        !!formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.dose_unit
                      }
                      onInputChange={(value) => {
                        formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.dose_unit`, value);
                      }}
                      onKeyDown={(e) => handleKeyPress(e, frequencyRef)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>
        <td className="text-left border-none">
          <div className="py-2 px-2 sm:px-3">
            <div className="relative rounded-md">
              <div className="inset-y-0 right-0 flex">
                <div className="flex items-center gap-2">
                  <div className={`relative ${showXValueInput ? "w-24" : "w-36"}`}>
                    <InputWithSuggestionBox
                      dropdownZIndex={dropdownZIndex}
                      showAllSuggestionsOnFocus={true}
                      forceSelection={true}
                      showDropdownIcon={true}
                      ref={frequencyRef}
                      inputName={`medications.${nestedIndex}.tapering_doses.${index}.frequency`}
                      mandatoryEmpty={isFrequencyRequiredAndEmpty}
                      suggestions={frequencySuggestions}
                      groupedSuggestions={frequencySuggestionGroups}
                      dropdownTextClassName="text-xs"
                      placeholder="Frequency"
                      defaultInputValue={formHook.getValues(
                        `medications.${nestedIndex}.tapering_doses.${index}.frequency`,
                      )}
                      formErrors={
                        !!formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.frequency ||
                        isFrequencyRequiredAndEmpty
                      }
                      onInputChange={(value) => {
                        formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.frequency`, value);
                        if (!frequencyXValueRequirementMap[value]) {
                          formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.x_value`, undefined);
                        }
                      }}
                      onKeyDown={(e) => handleKeyPress(e, timingRef)}
                    />
                  </div>
                  {showXValueInput && (
                    <Input
                      variant="TRANSPARENT"
                      type="number"
                      data-medicine-required-empty={isXRequiredAndEmpty ? "true" : undefined}
                      className="w-14"
                      inputClassName={`ring-1 focus:border-1 focus:ring-1 h-8 text-center ${
                        isXRequiredAndEmpty ? "!ring-red-500 !focus:ring-red-500" : ""
                      }`}
                      placeholder="X"
                      fieldError={formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.x_value}
                      errorMessages={[{ message: "X value is required.", type: "required" }]}
                      register={formHook.register(`medications.${nestedIndex}.tapering_doses.${index}.x_value`, {
                        validate: (value) => {
                          const frequency = formHook.getValues(
                            `medications.${nestedIndex}.tapering_doses.${index}.frequency`,
                          );
                          const isRequired = !!frequencyXValueRequirementMap[frequency];
                          if (!isRequired) return true;
                          return value && Number(value) > 0 ? true : "X value is required.";
                        },
                      })}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </td>
        <td>
          <div className="py-2 px-2 sm:px-3">
            <div>
              <div className="relative rounded-md">
                <div className="inset-y-0 right-0 flex">
                  {mode === "PDF_PREVIEW" ? (
                    <span className="text-xs w-32">
                      {formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.timing`)}
                    </span>
                  ) : (
                    <div className="relative">
                      <InputWithSuggestionBox
                        dropdownZIndex={dropdownZIndex}
                        showAllSuggestionsOnFocus={true}
                        forceSelection={true}
                        showDropdownIcon={true}
                        ref={timingRef}
                        inputName={`medications.${nestedIndex}.tapering_doses.${index}.timing`}
                        mandatoryEmpty={isTimingRequiredAndEmpty}
                        suggestions={timingSuggestions}
                        groupedSuggestions={timingSuggestionGroups}
                        placeholder="Timing"
                        defaultInputValue={formHook.getValues(
                          `medications.${nestedIndex}.tapering_doses.${index}.timing`,
                        )}
                        formErrors={
                          !!formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.timing ||
                          isTimingRequiredAndEmpty
                        }
                        onInputChange={(value) => {
                          formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.timing`, value);
                        }}
                        onKeyDown={(e) => handleKeyPress(e, remarkRef)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </td>
        <td className="text-left">
          <div className="py-2 px-2 sm:px-3">
            <div className="">
              <div className="relative rounded-md flex gap-3 items-center">
                <div className="relative w-24">
                  <Input
                    data-medicine-required-empty={isDurationRequiredAndEmpty ? "true" : undefined}
                    errorMessages={[
                      { message: "", type: "required" },
                      DURATION_NUMERIC_ERROR_MESSAGE,
                      DURATION_NO_DECIMAL_ERROR_MESSAGE,
                      DURATION_POSITIVE_ERROR_MESSAGE,
                    ]}
                    fieldError={
                      formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.duration
                    }
                    className="w-full"
                    variant="TRANSPARENT"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputClassName={`ring-1 focus:border-1 focus:ring-1 h-8 ${
                      isDurationRequiredAndEmpty ? "!ring-red-500 !focus:ring-red-500" : ""
                    }`}
                    placeholder={"e.g 5"}
                    register={formHook.register(`medications.${nestedIndex}.tapering_doses.${index}.duration`, {
                      required: isParentSkuFormMandatory,
                      ...DURATION_NUMBER_FORMAT_VALIDATION,
                      validate: {
                        noDecimal: (value) => {
                          if (value === null || value === undefined || String(value).trim() === "") {
                            return true;
                          }
                          return Number.isInteger(Number(value));
                        },
                        positiveValue: (value) => {
                          if (value === null || value === undefined || String(value).trim() === "") {
                            return true;
                          }
                          return Number(value) > 0;
                        },
                      },
                    })}
                  />
                </div>

                <div className="flex items-center h-8">
                  <span className="text-[#9CA3AF] font-semibold text-xs">days</span>
                </div>
              </div>
            </div>
          </div>
        </td>

        <td className="text-left">
          <div className="w-full py-2 px-2 sm:px-3">
            <Input
              errorMessages={[{ message: "Temperature is required.", type: "required" }]}
              fieldError={formHook.formState.errors.medications}
              register={formHook.register(`medications.${nestedIndex}.tapering_doses.${index}.remark`, {
                required: false,
              })}
              inputClassName="text-xs h-9"
              type="text"
              placeholder="Remarks"
            />
          </div>
        </td>
        <td className="p-2 sm:p-3">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              fieldArrayHook.remove(index);
            }}
            className="w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="white"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-5 text-[#ED0101]"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
              />
            </svg>
          </button>
        </td>
      </tr>
    );
  });
};

export default NestedTaperingDoseV2;
